// @ts-check

import { Select } from "antd";
import { CheckCircleTwoTone, CloseCircleTwoTone } from "@ant-design/icons";
import {
    get_assignment_filters,
    get_forms_filters,
    get_my_teams_data,
    get_team_promise,
    update_team_cookie,
} from '~/api/zero-api';
import { useCurrentUser } from '~/hooks/reduxHooks';
import { isAdmin, isAdminOrTeamLead, setParamDefault, TwoToneIconColor } from '~/other/Helper';
import NotificationAlert from '~/other/NotificationAlert';
import { useCallback, useEffect, useMemo, useState } from "react";
import TimePeriodFilter from '~/components/TimePeriodFilter';
import LocationFilter from '~/components/Shared/LocationFilter';
import useZeroSelector from '~/hooks/useZeroSelector';
import { useDispatch } from "react-redux";
import { updateFilterOptions, updateFormsFilterQuery, updateFormsPeriodQuery } from '~/store/actions/FormsActions';
import { update_current_team } from '~/store/actions/TeamsActions';
import useSessionStorageState from '~/hooks/useSessionStorageState';
import { useLocation, useHistory } from "react-router-dom";

const mainFilterKeys = /** @type {const} */ ([
    "form_uuid",
    "scheduler_uuid",
    "category_uuids",
    "status",
    "schedule_status",
    "schedule_only",
    "search",
]);
/** @typedef {typeof mainFilterKeys[number]} MainFilterKey */

/** @type {Record<MainFilterKey, FormsPageTab[]>} */
const excludeFilterForTabMap = {
    form_uuid: ["templates"],
    scheduler_uuid: ["templates", "schedules"],
    category_uuids: [],
    status: ["analytics", "submissions", "schedules", "templates"],
    schedule_status: ["analytics", "assignments", "submissions", "templates"],
    schedule_only: ["analytics", "assignments", "schedules", "submissions"],
    search: [],
};

/**
 * @param {{
 *  activeTab: FormsPageTab,
 *  pageType: FormsPageType,
 * }} props
 */
export default function FormsPageFilters({ activeTab, pageType }) {
    const location = useLocation();
    const user = useCurrentUser();
    const teams = useZeroSelector((state) => state.teams_helper.teams);
    const periodQuery = useZeroSelector((state) => state.forms.period_query);
    const formsQuery = useZeroSelector((state) => state.forms.query);
    const dispatch = useDispatch();

    const [loadingSubmissionFilters, setLoadingSubmissionFilters] = useState(false);
    const [submissionFilters, setSubmissionFilters] = useSessionStorageState("FormsPageFilters__submissionFilters", {
        forms: [],
        schedules: [],
        categories: [],
    });
    const [loadingAssignmentFilters, setLoadingAssignmentFilters] = useState(false);
    const [assignmentFilters, setAssignmentFilters] = useSessionStorageState("FormsPageFilters__assignmentFilters", {
        forms: [],
        schedules: [],
    });
    const [selectedLocation, setSelectedLocation] = useState("");
    const [selectedTeam, setSelectedTeam] = useState(
        isAdmin(user) ? "organization_teams" : "my_teams"
    );

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const formUuid = params.get("form_uuid");
        if (formUuid) {
            const formQuery = new URLSearchParams(formsQuery);
            formQuery.set("form_uuid", formUuid);
            dispatch(updateFormsFilterQuery(`?${formQuery}`));
        }
    }, [location.search]);

    const loading = loadingSubmissionFilters || loadingAssignmentFilters;

    const fetchSubmissionFilters = async () => {
        try {
            setLoadingSubmissionFilters(true);
            const res = await get_forms_filters(`?form_types=${pageType}`);
            const content = await res.json();
            setSubmissionFilters(content);
            dispatch(updateFilterOptions(content));
        } catch (err) {
            console.error(err);
            NotificationAlert("error", "", "Could not load submission filters");
        } finally {
            setLoadingSubmissionFilters(false);
        }
    };

    const fetchAssignmentFilters = async () => {
        if (!isAdminOrTeamLead(user)) {
            return;
        }

        try {
            setLoadingAssignmentFilters(true);
            const response = await get_assignment_filters(`?form_types=${pageType}`);
            const content = await response.json();
            const filters = {
                forms: content.forms,
                schedules: content.schedulers,
            };
            setAssignmentFilters(filters);
        } catch (err) {
            console.error(err);
            NotificationAlert("error", "", "Could not load assignment filters");
        } finally {
            setLoadingAssignmentFilters(false);
        }
    };

    const mainFilterOptions = useMemo(() => {
        /** @type {AntDSelectOptions} */
        const options = [];

        const forms = activeTab === "assignments" ? assignmentFilters.forms : submissionFilters.forms;
        const schedules = activeTab === "assignments" ? assignmentFilters.schedules : submissionFilters.schedules;

        const assignmentStatusOptions = {
            label: "Status",
            title: "status",
            options: [
                {
                    value: "status=completed",
                    label: "Completed",
                    renderedLabel: (
                        <>
                            <CheckCircleTwoTone twoToneColor={TwoToneIconColor.green} className="mar-rgt-5" /> Completed
                        </>
                    ),
                },
                {
                    value: "status=open",
                    label: "Open",
                    renderedLabel: (
                        <>
                            <CloseCircleTwoTone twoToneColor={TwoToneIconColor.grey} className="mar-rgt-5" /> Open
                        </>
                    ),
                },
                {
                    value: "status=missed",
                    label: "Missed",
                    renderedLabel: (
                        <>
                            <CloseCircleTwoTone twoToneColor={TwoToneIconColor.red} className="mar-rgt-5" /> Missed
                        </>
                    ),
                },
                {
                    value: "status=excused",
                    label: "Excused",
                    renderedLabel: (
                        <>
                            <CloseCircleTwoTone twoToneColor={TwoToneIconColor.yellow} className="mar-rgt-5" /> Excused
                        </>
                    ),
                },
            ],
        };

        const categoryOptions = {
            label: "Category",
            title: "category",
            options: submissionFilters.categories.map((category) => ({
                value: `category_uuids=${category.category_uuid}`,
                label: category.category_name,
            })),
        };

        const scheduleOnlyOptions = {
            label: "Type",
            title: "type",
            options: [
                { value: "schedule_only=false", label: "Regular" },
                { value: "schedule_only=true", label: "Schedule Only" },
            ],
        };

        const templateOptions = {
            label: pageType === "lms" ? "Course Name" : "Template Name",
            title: "form",
            options: forms.map((form) => ({
                value: `form_uuid=${form.form_uuid || form.uuid}`,
                label: form.form_name || form.name,
            })),
        };

        const scheduleOptions = {
            label: "Schedule Name",
            title: "schedule",
            options: schedules.map((schedule) => ({
                value: `scheduler_uuid=${schedule.scheduler_uuid || schedule.uuid}`,
                label: schedule.scheduler_name || schedule.name,
            })),
        };

        const scheduleStatusOptions = {
            label: "Schedule Status",
            title: "scheduleStatus",
            options: [
                { value: "schedule_status=active", label: "Active" },
                { value: "schedule_status=paused", label: "Paused" },
            ],
        };

        const showAssignmentStatusOptions = !excludeFilterForTabMap.status.includes(activeTab);
        const showScheduleOnlyOptions = !excludeFilterForTabMap.schedule_only.includes(activeTab);
        const showCategoryOptions = pageType === "regular" && categoryOptions.options.length > 0;
        const showTemplateOptions = !excludeFilterForTabMap.form_uuid.includes(activeTab);
        const showScheduleOptions = !excludeFilterForTabMap.scheduler_uuid.includes(activeTab);
        const showScheduleStatusOptions = !excludeFilterForTabMap.schedule_status.includes(activeTab);

        options.push(
            showAssignmentStatusOptions && assignmentStatusOptions,
            showScheduleOnlyOptions && scheduleOnlyOptions,
            showScheduleStatusOptions && scheduleStatusOptions,
            showCategoryOptions && categoryOptions,
            showTemplateOptions && templateOptions,
            showScheduleOptions && scheduleOptions
        );

        return options.filter((opt) => !!opt);
    }, [submissionFilters, assignmentFilters, activeTab, pageType]);

    const teamFilterOptions = useMemo(() => {
        const includeMySubmissionsOption = ["submissions", "analytics", "assignments"].includes(activeTab);

        const specialTeamOptions = [
            isAdmin(user) && { value: "organization_teams", label: "All Account Teams" },
            { value: "my_teams", label: "All My Teams" },
            includeMySubmissionsOption && {
                value: "my_submissions",
                label: activeTab === "assignments" ? "My Assignments" : "My Submissions",
            },
        ];

        /** @type {AntDSelectOptions} */
        const options = specialTeamOptions;
        if (teams.length > 0) {
            options.push({
                label: "Teams",
                title: "teams",
                options: teams.map((team) => ({
                    value: team.uuid,
                    label: team.name,
                })),
            });
        }

        return options.filter((opt) => !!opt);
    }, [user, activeTab, teams]);

    const disabledFilters = useMemo(() => {
        /**
         * @param {boolean} main
         * @param {boolean} time
         * @param {boolean} location
         * @param {boolean} teams
         */
        const generateDisabledFilters = (main, time, location, teams) => ({
            main,
            time,
            location,
            teams,
        });

        if (activeTab === "reports" || activeTab === "settings") {
            return generateDisabledFilters(true, true, true, true);
        }

        if (activeTab === "templates" || activeTab === "schedules") {
            return generateDisabledFilters(false, true, true, false);
        }

        return generateDisabledFilters(false, false, false, false);
    }, [activeTab]);

    useEffect(() => {
        fetchSubmissionFilters();
        fetchAssignmentFilters();
    }, [pageType]);

    const changeLocation = (newLocation) => {
        setSelectedLocation(newLocation);

        const query = new URLSearchParams(formsQuery);

        if (newLocation) {
            query.set("location_uuid", selectedLocation);
        } else {
            query.delete("location_uuid");
        }

        dispatch(updateFormsFilterQuery(`?${query}`));
    };

    const changeTeam = (teamId) => {
        setSelectedTeam(teamId);
        update_team_cookie(teamId);

        const errHandler = () => {
            const no_team = {
                uuid: "Unknown",
                name: "Unknown",
                description: "",
                users_count: 0,
            };
            dispatch(update_current_team(no_team));
        };

        if (teamId === "my_teams" || teamId === "my_submissions") {
            get_my_teams_data()
                .then((res) => res.json())
                .then((content) => dispatch(update_current_team(content.team)))
                .catch(errHandler);
        } else if (teamId === "organization_teams") {
            const team = {
                uuid: "organization_teams",
                name: "All Account Teams",
                description: "",
                users_count: 0,
            };
            dispatch(update_current_team(team));
        } else {
            get_team_promise(teamId)
                .then((res) => res.json())
                .then((content) => dispatch(update_current_team(content.team)))
                .catch(errHandler);
        }

        const query = new URLSearchParams(formsQuery);

        if (teamId === "my_submissions") {
            query.set("team_uuid", "my_teams");
            query.set("my_submissions", "true");
        } else {
            query.set("team_uuid", teamId);
            query.delete("my_submissions");
        }

        dispatch(updateFormsFilterQuery(`?${query}`));
    };

    const changeMainFilters = (values) => {
        /** @type {Record<MainFilterKey, string[]>} */
        // @ts-ignore
        const filters = mainFilterKeys.reduce((acc, key) => {
            acc[key] = [];
            return acc;
        }, {});

        const addPrefixedValue = (value) => {
            for (const filterKey of mainFilterKeys) {
                const prefix = `${filterKey}=`;
                if (value.startsWith(prefix)) {
                    filters[filterKey].push(value.replace(prefix, ""));
                    return true;
                }
            }
            return false;
        };

        for (const value of values) {
            if (addPrefixedValue(value)) {
                continue;
            }
            filters.search.push(value);
        }

        const query = new URLSearchParams(formsQuery);

        for (const [key, value] of Object.entries(filters)) {
            if (value.length > 0) {
                query.set(key, value.join(","));
            } else {
                query.delete(key);
            }
        }

        dispatch(updateFormsFilterQuery(`?${query}`));
    };

    const mainFilters = useMemo(() => {
        let optionValues = [];

        const params = new URLSearchParams(formsQuery);
        // @ts-ignore
        for (const [key, queryValue] of params.entries()) {
            if (key in excludeFilterForTabMap && excludeFilterForTabMap[key].includes(activeTab)) {
                continue;
            }

            const values = queryValue.split(",");
            for (const value of values) {
                if (key === "search") {
                    optionValues.push(value);
                } else if (mainFilterKeys.includes(key)) {
                    optionValues.push(`${key}=${value}`);
                }
            }
        }

        return optionValues;
    }, [formsQuery, activeTab]);

    return (
        <>
            <div className="row" style={{ padding: "0 0.5rem" }}>
                <Select
                    mode="tags"
                    placeholder={"Filter or search..."}
                    suffixIcon={null}
                    virtual={false}
                    value={mainFilters}
                    onChange={changeMainFilters}
                    options={mainFilterOptions}
                    optionFilterProp="label"
                    optionRender={renderOption}
                    disabled={disabledFilters.main}
                    loading={loading}
                    className="col-xs-12 col-sm-6 col-reduce-padding"
                    style={{ marginTop: "0.8rem" }}
                />
                <div className="col-xs-12 col-sm-6 col-reduce-padding" style={{ marginTop: "0.8rem" }}>
                    <TimePeriodFilter
                        noWrapperStyle={true}
                        period_query={periodQuery}
                        updateQuery={(query) => dispatch(updateFormsPeriodQuery(query))}
                        periodSelected={() => {}}
                        disabled={disabledFilters.time}
                    />
                </div>
            </div>
            <div className="row" style={{ padding: "0 0.5rem" }}>
                <div className="col-xs-12 col-sm-6 col-reduce-padding" style={{ marginTop: "0.8rem" }}>
                    <LocationFilter
                        style={{ width: "100%" }}
                        value={selectedLocation}
                        onLocationSelected={changeLocation}
                        disabled={disabledFilters.location}
                    />
                </div>
                <div className="col-xs-12 col-sm-6 col-reduce-padding" style={{ marginTop: "0.8rem" }}>
                    <Select
                        value={selectedTeam}
                        onChange={changeTeam}
                        virtual={false}
                        style={{ width: "100%" }}
                        options={teamFilterOptions}
                        optionFilterProp="label"
                        disabled={disabledFilters.teams}
                    />
                </div>
            </div>
        </>
    );
}

/** @type {AntDSelectOptionRender} */
function renderOption(option, info) {
    if ("renderedLabel" in option.data) {
        // @ts-ignore
        return option.data.renderedLabel;
    }

    return option.label;
}
